import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/formatDate';
import { RefreshCw, Search } from 'lucide-react';

export const DebugLogs = () => {
  const { fetchAuthorized } = useAuth();
  const [logs, setLogs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [deviceFilter, setDeviceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [offset, setOffset]     = useState(0);
  const LIMIT = 50;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: LIMIT, offset });
    if (deviceFilter.trim()) params.set('device_id', deviceFilter.trim());
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo)   params.set('date_to',   dateTo);

    fetchAuthorized(`/api/dashboard/debug-logs?${params}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) { setLogs(d.logs || []); setTotal(d.total || 0); }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fetchAuthorized, deviceFilter, dateFrom, dateTo, offset]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-1)' }}>
          Журнал дебаг-доступов
        </h1>
        <button
          className="btn-icon"
          onClick={() => { setOffset(0); load(); }}
          title="Обновить"
          style={{ padding: '8px' }}
        >
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
        </button>
      </div>

      {/* Фильтры */}
      <form onSubmit={handleSearch} style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-2)' }}>ID устройства</label>
          <input
            className="search-input"
            style={{ width: '200px' }}
            value={deviceFilter}
            onChange={e => setDeviceFilter(e.target.value)}
            placeholder="device_id"
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-2)' }}>Дата с</label>
          <input
            type="date"
            className="search-input"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-2)' }}>Дата по</label>
          <input
            type="date"
            className="search-input"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
        </div>
        <button
          type="submit"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
        >
          <Search size={14} />
          Найти
        </button>
      </form>

      <div className="glass-card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>Загрузка...</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
            Нет записей
          </div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Сервисник', 'Telegram', 'Устройство', 'Время'].map((h, i) => (
                    <th key={i} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', color: 'var(--text-3)', fontWeight: 600 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-1)' }}>
                      {log.full_name || '—'}
                      {log.username && <span style={{ color: 'var(--text-2)', marginLeft: '6px' }}>@{log.username}</span>}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text-2)', fontFamily: 'monospace' }}>
                      {log.telegram_id || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text-2)' }}>
                      {log.device_name || log.machine_name || log.device_id || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-2)' }}>
                      {fmtDate(log.opened_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {total > LIMIT && (
              <div style={{ padding: '12px 16px', display: 'flex', gap: '8px', justifyContent: 'center', borderTop: '1px solid var(--border)' }}>
                <button
                  disabled={offset === 0}
                  onClick={() => setOffset(o => Math.max(0, o - LIMIT))}
                  style={{ padding: '6px 16px', border: '1px solid var(--border)', borderRadius: '6px', background: 'transparent', color: 'var(--text-1)', cursor: 'pointer', fontSize: '13px' }}
                >
                  ← Назад
                </button>
                <span style={{ lineHeight: '32px', fontSize: '13px', color: 'var(--text-2)' }}>
                  {offset + 1}–{Math.min(offset + LIMIT, total)} из {total}
                </span>
                <button
                  disabled={offset + LIMIT >= total}
                  onClick={() => setOffset(o => o + LIMIT)}
                  style={{ padding: '6px 16px', border: '1px solid var(--border)', borderRadius: '6px', background: 'transparent', color: 'var(--text-1)', cursor: 'pointer', fontSize: '13px' }}
                >
                  Вперёд →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
